import { ChangeEvent, useContext, useEffect, useState } from 'react';
import {  Button, TableCell, TableRow, TableContainer, Table, TableHead, TableBody, Dialog,  DialogActions,  FormControlLabel, Checkbox, FormGroup, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { PrimaryCategoryStockCount } from '../../../../app/services/api/generated';
import { Check } from  '../../../../assets';
import useLogError from '../../../../hooks/useLogError';
import { ScheduleCreateContext, ScheduleCreateValues } from '../../contexts/ScheduleCreateContext';
import CloseIcon from '@mui/icons-material/Close';

const ScheduleCreateCategoriesModal=() => {
  const { t,
    isCategoriesModalOpen,
    handleClose,
    categoryData,
    handleCategories } = useContext(ScheduleCreateContext) as ScheduleCreateValues;

  const { selectedStore } = useContext(UserContext);
  const { getCategories } = useNswagClient();
  const [dataFromApi, setDataFromApi] = useState<PrimaryCategoryStockCount[] | undefined>([]);
  const [isChecked, setIsChecked] = useState(false);
  const [tempCategoryData, setTempCategoryData] = useState<PrimaryCategoryStockCount[] | undefined>([]);
  const [isSelectAllSmartChange, setIsSelectAllSmartChange] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const { logError } = useLogError();
  
  useEffect(() => {
    getData();
  }, []);

  const addItemToTempList = (item : PrimaryCategoryStockCount) => {
    setTempCategoryData([...tempCategoryData!, item]);
  };
  
  const handleConfirm = () => {
    setTempCategoryData([]);
    handleCategories(tempCategoryData || []);
  };

  const handleCategoryCheckboxChange = (category: string, isChecked: boolean ) => {
    if (isChecked) {
      const exist = tempCategoryData?.find(row => row.primaryCategoryName === category);
      if (!exist) {
        const selectedCategory = dataFromApi?.find(row => row.primaryCategoryName === category);
        if (selectedCategory) {
          addItemToTempList(selectedCategory);
        }
      }
    } else {
      setTempCategoryData(prevData => {
        setIsChecked(false);
        const updatedData = prevData?.filter(row => row.primaryCategoryName !== category) || [];
        return updatedData;
      });
    }
  };

  const handleSelectAllChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      setTempCategoryData(dataFromApi); 
      setIsSelectAllSmartChange(true);
      setIsSelectAll(true);
    } else {
      setTempCategoryData([]);
      setIsSelectAllSmartChange(false);
      setIsSelectAll(false);
    }
  };

  const handleSelectSmartChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const smartdata = dataFromApi?.filter(item => item.smartCount);
    if (checked) {
      setIsSelectAllSmartChange(true);
      setTempCategoryData(isSelectAll ? dataFromApi : smartdata) ;
    } else {
      const uncheckedData = dataFromApi?.filter(item => !smartdata?.includes(item)) || [];
      setTempCategoryData(isSelectAll ? uncheckedData : []);
      setIsSelectAllSmartChange(false);
    }
  };

  const getData = () => {
    const storeNumber = selectedStore?.storeNumber ?? '';
    getCategories(storeNumber).then((result) => {
      if (result) {  
        setDataFromApi(result.data?.sort((a, b) => a.primaryCategoryName?.localeCompare(b.primaryCategoryName??'') ?? 0));
      } else {
        logError(result);
      }})
      .catch((error) => {
        logError(error);
      });
  };

  const close = () => {
    setTempCategoryData([]);
    handleClose();
  };

  return (
    <Dialog open={isCategoriesModalOpen}
      onClose={close}
      maxWidth='md'
      fullWidth>
      <DialogTitle>
        {t('categories')}
        <IconButton
          onClick={close}
          sx={{ position: 'absolute',
            right: 16,
            top: 8,
            color: (theme) => theme.palette.custom.gray[800],
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TableContainer sx={{ overflowy:'auto', border: '1px solid' }}
          style={{ maxHeight: '400px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell >{t('category')}</TableCell>
                <TableCell >{t('items')}</TableCell>
                <TableCell >{t('smartCount')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataFromApi?.map((row) => (
                <TableRow key={row.id}>
                  <TableCell style={{ display: 'flex', alignItems: 'center' }}  >
                    <Checkbox
                      color="primary"
                      inputProps={{
                        'aria-label': `select category ${row.primaryCategoryName}`,
                      }}
                      checked={isChecked || tempCategoryData?.some((item) => item.primaryCategoryName === row.primaryCategoryName) || categoryData?.some((item) => item.primaryCategoryName === row.primaryCategoryName)}
                      onChange={(e) => handleCategoryCheckboxChange(row.primaryCategoryName!, e.target.checked)}/>
                    {row.primaryCategoryName}
                  </TableCell>
                  <TableCell >{row.itemCount}</TableCell>
                  <TableCell >{row.smartCount === true ? <Check /> : row.smartCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <FormGroup sx={{ pt:'10px' }}>
          <FormControlLabel
            control={<Checkbox 
              onChange={handleSelectSmartChange} />}
            checked={isSelectAllSmartChange}
            label={isSelectAllSmartChange ? t('removeSmartCategories') : t('addSmartCategories')} />
          <FormControlLabel 
            control={<Checkbox onChange={handleSelectAllChange} />}
            label={t('addAllCategories')} />
        </FormGroup>
      </DialogContent>
      <DialogActions >
        <Button 
          variant="secondary"
          size='lg'
          onClick={close}>
          {t('cancel')}
        </Button>
        <Button
          size='lg'
          onClick={handleConfirm}>
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
    
  );
};

export default ScheduleCreateCategoriesModal;